import { Timeline } from "antd";
import { useState } from "react";

import { BTText } from "@btnet/core-components";

import { BdsIconCaretSmallDown, BdsIconCaretSmallRight } from "@buildertrend/components";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { CKEditorDisplay } from "commonComponents/utilities/CKEditorDisplay/CKEditorDisplay";

import { type EntityChangeEvent, type PropertyChangeDisplay } from "handlers";

import { type IActivityEvent } from "./ActivityLog.types";

const renderLabel = (name: string) =>
    name ? (
        <BTText tag="span" className="property-name">
            {name}:{" "}
        </BTText>
    ) : null;

const renderValue = (value: string, className: string) => (
    <BTText tag="span" className={className}>
        <CKEditorDisplay dangerouslySetInnerHTML={{ __html: value }} />
    </BTText>
);

interface IActivityLogItemProps {
    event: IActivityEvent;
    index: number;
    detailsLabel?: string;
}

export const ActivityLogItem: React.FC<IActivityLogItemProps> = ({
    event,
    detailsLabel = "changes",
}) => {
    const [showDetails, setShowDetails] = useState(false);
    const hasPropertyChanges = event.propertyChanges && event.propertyChanges.length > 0;
    const hasRelatedChanges = event.relatedTypeChanges && event.relatedTypeChanges.length > 0;
    const hasAnyChanges = hasPropertyChanges || hasRelatedChanges;

    // Count only direct children, not grandchildren (nested approvers are details, not separate changes)
    const totalChangeCount =
        (event.propertyChanges?.length || 0) + (event.relatedTypeChanges?.length || 0);

    const parentOperationType = event.operationType?.toUpperCase();

    const renderPropertyChange = (
        prop: PropertyChangeDisplay,
        idx: number,
        relatedOperationType?: string,
    ) => {
        const displayName = prop.displayName ?? "";
        const oldValue = prop.oldValue || "(empty)";
        const newValue = prop.newValue || "(empty)";
        const operationType = relatedOperationType || parentOperationType;

        // Label only: No old/new values, just display the name (e.g., "Description updated")
        if (!prop.oldValue && !prop.newValue) {
            return (
                <div key={idx} className="property-change-item">
                    <BTText tag="span" className="property-name">
                        {displayName}
                    </BTText>
                </div>
            );
        }

        // PARTICIPANTSCHANGED: the newValue is a pre-rendered HTML list of the resulting participants
        // with added names in <strong> and removed names in <s>. Render it as one inline-highlighted
        // value rather than the whole-value bold/strikethrough used by INSERT/DELETE/UPDATE.
        if (operationType === "PARTICIPANTSCHANGED") {
            return (
                <div key={idx} className="property-change-item">
                    {renderLabel(displayName)}
                    {renderValue(newValue, "property-participants-value")}
                </div>
            );
        }

        // INSERT: Only show new values (old values are always empty)
        if (operationType === "INSERT") {
            return (
                <div key={idx} className="property-change-item">
                    {renderLabel(displayName)}
                    {renderValue(newValue, "property-new-value")}
                </div>
            );
        }

        // DELETE: Only show old values (new values are always empty)
        if (operationType === "DELETE") {
            return (
                <div key={idx} className="property-change-item">
                    {renderLabel(displayName)}
                    {renderValue(oldValue, "property-old-value")}
                </div>
            );
        }

        // Field cleared within an update (had a value, now empty): show only the struck-through old
        // value rather than "old → (empty)". The event description conveys that it was removed.
        if (prop.oldValue && !prop.newValue) {
            return (
                <div key={idx} className="property-change-item">
                    {renderLabel(displayName)}
                    {renderValue(oldValue, "property-old-value")}
                </div>
            );
        }

        // Field set from empty (had no prior value): show only the new value rather than
        // "(empty) → new". The event description conveys that it was added.
        if (!prop.oldValue && prop.newValue) {
            return (
                <div key={idx} className="property-change-item">
                    {renderLabel(displayName)}
                    {renderValue(newValue, "property-new-value")}
                </div>
            );
        }

        // UPDATE: Show old → new (default behavior)
        return (
            <div key={idx} className="property-change-item">
                {renderLabel(displayName)}
                <span className="property-values">
                    {renderValue(oldValue, "property-old-value")}
                    <BTText tag="span" className="property-arrow">
                        {" "}
                        →{" "}
                    </BTText>
                    {renderValue(newValue, "property-new-value")}
                </span>
            </div>
        );
    };

    const renderRelatedTypeChange = (relatedEvent: EntityChangeEvent, idx: number) => {
        const relatedOperationType = relatedEvent.operationType?.toUpperCase();
        const hasNestedChanges =
            relatedEvent.relatedTypeChanges && relatedEvent.relatedTypeChanges.length > 0;
        const hasDescription = !!relatedEvent.changeDescription;

        // Items with no description (like individual approvers) should have tighter spacing
        const className = hasDescription
            ? "activity-log-related-change"
            : "activity-log-related-change activity-log-related-change--property-only";

        return (
            <div key={`related-${idx}`} className={className}>
                <div className="related-change-content">
                    {hasDescription && (
                        <BTText className="related-change-description">
                            {relatedEvent.changeDescription}
                        </BTText>
                    )}

                    {relatedEvent.propertyChanges?.map((prop, propIdx) => (
                        <div key={`related-prop-${propIdx}`} className="related-property-change">
                            {renderPropertyChange(prop, propIdx, relatedOperationType)}
                        </div>
                    ))}

                    {hasNestedChanges &&
                        relatedEvent.relatedTypeChanges?.map((nestedEvent, nestedIdx) =>
                            renderRelatedTypeChange(nestedEvent, nestedIdx),
                        )}
                </div>
            </div>
        );
    };

    return (
        <Timeline.Item key={`event-${event.id}`} dot={event.icon}>
            <div>
                <BTText size="normal-md" className="activity-log-main">
                    {event.message}
                </BTText>

                {hasAnyChanges && (
                    <div className="activity-log-details">
                        <BTButtonDeprecated
                            className="activity-log-toggle"
                            onClick={() => setShowDetails(!showDetails)}
                            data-testid={`activity-log-toggle-${event.id}`}
                        >
                            {showDetails ? (
                                <BdsIconCaretSmallDown size="sm" />
                            ) : (
                                <BdsIconCaretSmallRight size="sm" />
                            )}

                            <BTText tag="span">
                                {showDetails ? "Hide" : "Show"} {detailsLabel} ({totalChangeCount})
                            </BTText>
                        </BTButtonDeprecated>

                        {showDetails && (
                            <div className="activity-log-changes-container">
                                {hasPropertyChanges && (
                                    <div className="activity-log-property-changes">
                                        {event.propertyChanges?.map((prop, idx) =>
                                            renderPropertyChange(prop, idx),
                                        )}
                                    </div>
                                )}

                                {hasRelatedChanges && (
                                    <div className="activity-log-related-changes">
                                        {event.relatedTypeChanges?.map((relatedEvent, idx) =>
                                            renderRelatedTypeChange(relatedEvent, idx),
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Timeline.Item>
    );
};
